// Meta Ads connection settings + event log (NEW per user request).
import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { Plus, Trash2, AlertCircle, CheckCircle, Copy } from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path, opts = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

export default function MetaAdsSettingsView({ currentUser, showToast = () => {} }) {
  const [data, setData]     = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [adding, setAdding] = React.useState(false);
  const [f, setF]           = React.useState({ page_id: '', page_name: '', page_access_token: '', form_ids: '', default_vertical: '', default_lead_type: 'general', auto_ai_followup: true });

  const load = React.useCallback(async () => {
    setLoading(true);
    try { setData(await apiFetch('/api/meta-ads/config')); }
    catch (err) { showToast(`Load failed: ${err.message}`); }
    finally { setLoading(false); }
  }, [showToast]);
  React.useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!f.page_id.trim() || !f.page_access_token.trim()) { showToast('Page ID + Page Access Token required.'); return; }
    try {
      await apiFetch('/api/meta-ads/config', {
        method: 'POST',
        body: JSON.stringify({
          ...f,
          form_ids: f.form_ids ? f.form_ids.split(',').map(s => s.trim()).filter(Boolean) : [],
          default_vertical: f.default_vertical || null,
        }),
      });
      showToast('Meta Page connected.');
      setF({ page_id: '', page_name: '', page_access_token: '', form_ids: '', default_vertical: '', default_lead_type: 'general', auto_ai_followup: true });
      setAdding(false);
      load();
    } catch (err) { showToast(`Save failed: ${err.message}`); }
  };

  const remove = async (id) => {
    if (!window.confirm('Disconnect this Meta Page? Leads will stop flowing.')) return;
    try { await apiFetch(`/api/meta-ads/config?id=${id}`, { method: 'DELETE' }); load(); }
    catch (err) { showToast(`Delete failed: ${err.message}`); }
  };

  if (loading) return <div className="text-sm text-slate-500 p-4">Loading…</div>;

  const pages  = data?.pages || [];
  const events = data?.recent_events || [];
  const webhookUrl = data?.webhook_url || '';
  const verifyTokenOk = !!data?.verify_token_configured;

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-slate-900 to-blue-900 text-white rounded-xl p-5">
        <div className="text-blue-300 text-[0.55rem] font-black uppercase tracking-[0.3em]">Meta Lead Ads · Inbound</div>
        <h1 className="text-xl font-bold mt-1">Auto-flow leads from your Facebook + Instagram ads</h1>
        <p className="text-slate-300 text-sm mt-2">When someone fills your in-ad form, the lead lands here within seconds. If "Auto AI follow-up" is on, the AI Lead Follow-up agent texts them immediately and works the cadence.</p>
      </div>

      {/* Webhook setup info — operator pastes these into Meta */}
      <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4">
        <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800 mb-2">One-time Meta setup (do this first)</div>
        <ol className="text-sm text-amber-900 space-y-2 list-decimal list-inside">
          <li>
            Go to <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="text-amber-900 underline">developers.facebook.com/apps</a> → Create / open your App → <strong>Webhooks</strong> product.
          </li>
          <li>
            Subscribe to <strong>Page</strong> object → field <code className="bg-white border border-amber-300 rounded px-1">leadgen</code>.
          </li>
          <li>
            <div>Callback URL:</div>
            <div className="flex items-center gap-2 mt-1">
              <code className="bg-white border border-amber-300 rounded px-2 py-1 text-xs font-mono flex-1 break-all">{webhookUrl}</code>
              <button onClick={async () => { try { await navigator.clipboard.writeText(webhookUrl); showToast('Copied.'); } catch {} }} className="text-amber-800 hover:text-amber-900"><Copy size={14}/></button>
            </div>
          </li>
          <li>
            <strong>Verify Token:</strong> paste the value of your <code className="bg-white border border-amber-300 rounded px-1">META_WEBHOOK_VERIFY_TOKEN</code> env var (set on Vercel) into Meta's verify field. {verifyTokenOk ? <span className="text-emerald-700">✓ Token configured on server</span> : <span className="text-rose-700">⚠ Token NOT yet set on Vercel</span>}.
          </li>
          <li>
            In your Page → Settings → New Pages Experience → Page Access Token → generate a long-lived token (requires <code className="bg-white border border-amber-300 rounded px-1">pages_read_engagement</code>, <code className="bg-white border border-amber-300 rounded px-1">pages_show_list</code>, <code className="bg-white border border-amber-300 rounded px-1">leads_retrieval</code>) and paste it below.
          </li>
          <li>
            Subscribe your Page to the App's leadgen webhook in App Dashboard → Webhooks → Page → "Add Subscription" → select your Page.
          </li>
        </ol>
      </div>

      {/* Connected pages */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700">Connected Pages ({pages.length})</div>
          <button onClick={() => setAdding(!adding)} className="text-xs font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"><Plus size={12}/> Connect a Page</button>
        </div>
        {adding && (
          <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <input value={f.page_id} onChange={e => setF({ ...f, page_id: e.target.value })} placeholder="Facebook Page ID (numeric)" className="text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
              <input value={f.page_name} onChange={e => setF({ ...f, page_name: e.target.value })} placeholder="Page name (label)" className="text-sm border border-slate-300 rounded-md px-2 py-1.5"/>
            </div>
            <input value={f.page_access_token} onChange={e => setF({ ...f, page_access_token: e.target.value })} placeholder="Page Access Token (long-lived, leads_retrieval scope)" className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5 font-mono"/>
            <input value={f.form_ids} onChange={e => setF({ ...f, form_ids: e.target.value })} placeholder="Form IDs (comma-separated, optional — empty = all forms)" className="w-full text-sm border border-slate-300 rounded-md px-2 py-1.5 font-mono"/>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <select value={f.default_vertical} onChange={e => setF({ ...f, default_vertical: e.target.value })} className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white">
                <option value="">Vertical (auto)</option>
                <option value="life">Life</option>
                <option value="health">Health</option>
              </select>
              <select value={f.default_lead_type} onChange={e => setF({ ...f, default_lead_type: e.target.value })} className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white">
                <option value="general">General</option>
                <option value="iul">IUL</option>
                <option value="mortgage_protection">Mortgage Protection</option>
                <option value="final_expense">Final Expense</option>
                <option value="veteran">Veteran</option>
                <option value="term">Term</option>
                <option value="whole">Whole Life</option>
                <option value="aca">ACA</option>
                <option value="medicare">Medicare</option>
              </select>
              <label className="text-xs text-slate-700 inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1.5">
                <input type="checkbox" checked={f.auto_ai_followup} onChange={e => setF({ ...f, auto_ai_followup: e.target.checked })}/>
                Auto AI follow-up (recommended)
              </label>
            </div>
            <div className="flex gap-2">
              <button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-4 py-1.5 rounded-md">Save Page</button>
              <button onClick={() => setAdding(false)} className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5">Cancel</button>
            </div>
          </div>
        )}
        {pages.length === 0 ? (
          <div className="px-4 py-6 text-sm text-slate-500 italic">No Pages connected yet. Follow the Meta setup steps above, then click "Connect a Page."</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {pages.map(p => (
              <div key={p.id} className="px-4 py-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-slate-900 flex items-center gap-2">
                    {p.page_name || p.page_id}
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${p.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : p.status === 'error' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-slate-50 text-slate-700 border-slate-200'}`}>{p.status}</span>
                    {p.auto_ai_followup && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">Auto AI text</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 font-mono">{p.page_id}{p.form_ids?.length > 0 && ` · forms: ${p.form_ids.join(', ')}`}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{p.last_lead_at ? `Last lead: ${new Date(p.last_lead_at).toLocaleString()}` : 'No leads yet'}{p.last_error && <span className="text-rose-700"> · {p.last_error}</span>}</div>
                </div>
                <button onClick={() => remove(p.id)} className="text-slate-300 hover:text-rose-700"><Trash2 size={14}/></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent webhook events log */}
      {events.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2 border-b border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-700">Recent webhook events</div>
          <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {events.map(e => (
              <div key={e.id} className="px-4 py-2 text-xs">
                <div className="flex items-center gap-2">
                  {e.status === 'processed' ? <CheckCircle size={11} className="text-emerald-600"/> : <AlertCircle size={11} className={e.status === 'failed' ? 'text-rose-600' : 'text-slate-400'}/>}
                  <span className="font-mono text-slate-700">{e.leadgen_id}</span>
                  <span className="text-slate-500">· {e.status}</span>
                  <span className="text-slate-400 ml-auto">{new Date(e.created_at).toLocaleString()}</span>
                </div>
                {e.error && <div className="text-rose-700 ml-4 mt-0.5">{e.error}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
